/**
 * HealthLoop Feature Index
 * -----------------------------------------------------------------------------
 * Static, lightweight map of in-app features used by the Global Search
 * (see /components/GlobalSearch.jsx). Keeping this as a plain JS module means:
 *   - zero network calls / zero backend infrastructure
 *   - tree-shakeable, fast to load on mobile
 *   - trivially extensible as new features ship
 *
 * Each entry intentionally carries a generous set of `keywords` so users who
 * "don't know what it's called" can still find a feature by intent
 * (e.g. typing "steps" surfaces the Daily Activity Tracker even though the
 * formal route is /step-tracker).
 *
 * The `section` field aligns 1:1 with the BottomNav tabs so search results can
 * be grouped exactly the way the rest of the app organises itself:
 *   Home · Nutrition · Family · Services · Support · Profile
 *
 * `status: 'coming-soon'` flags MVP-deferred features so the UI can render a
 * subtle badge — keeps expectations honest without hiding the feature from
 * search (users still get the navigation breadcrumb to where it lives).
 */

import {
  Activity,
  Apple,
  Baby,
  Bell,
  BookOpen,
  Brain,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Droplet,
  Footprints,
  HeartPulse,
  HelpCircle,
  Home,
  Languages,
  LineChart,
  Lock,
  Mars,
  Pill,
  Salad,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Target,
  TrendingUp,
  User,
  Users,
  Venus,
  Wind,
} from 'lucide-react';

export const FEATURE_INDEX = [
  // ===================================================================
  // HOME — daily wellness, check-ins, trackers, overview
  // ===================================================================
  {
    id: 'home',
    title: 'Home Dashboard',
    description: 'Your daily wellness overview and streaks.',
    route: '/dashboard',
    section: 'Home',
    icon: Home,
    keywords: ['home', 'dashboard', 'overview', 'daily', 'today', 'streak', 'main'],
  },
  {
    id: 'smart-check-in',
    title: 'Smart Daily Check-in',
    description: 'A 60-second check-in on mood, energy, hydration and sleep.',
    route: '/smart-check-in',
    section: 'Home',
    icon: CheckCircle2,
    keywords: ['checkin', 'check in', 'daily', 'mood', 'energy', 'log', 'journal', 'how am i', 'feeling'],
  },
  {
    id: 'quick-check',
    title: 'Quick Health Check',
    description: 'Lightweight in-the-moment wellness pulse.',
    route: '/quick-check',
    section: 'Home',
    icon: HeartPulse,
    keywords: ['quick', 'pulse', 'fast', 'snapshot', 'now', 'health'],
  },
  {
    id: 'step-tracker',
    title: 'Daily Activity Tracker',
    description: 'Track steps, movement and fitness goals.',
    route: '/step-tracker',
    section: 'Home',
    icon: Footprints,
    keywords: ['steps', 'step', 'walk', 'walking', 'activity', 'fitness', 'movement', 'exercise', 'goal', 'pedometer', 'cardio'],
  },
  {
    id: 'habits',
    title: 'Wellness Habits',
    description: 'Build and track small daily habits.',
    route: '/habits',
    section: 'Home',
    icon: Target,
    keywords: ['habits', 'routines', 'goals', 'consistency', 'streak', 'tracking'],
  },
  {
    id: 'trends',
    title: 'Trends & Insights',
    description: 'See how your wellness has shifted over time.',
    route: '/trends',
    section: 'Home',
    icon: TrendingUp,
    keywords: ['trends', 'history', 'analytics', 'progress', 'over time', 'graph', 'chart'],
  },
  {
    id: 'progress',
    title: 'Progress Tracker',
    description: 'Visualise your wellness milestones.',
    route: '/progress',
    section: 'Home',
    icon: LineChart,
    keywords: ['progress', 'milestones', 'achievements', 'tracking'],
  },
  {
    id: 'insights',
    title: 'Personal Insights',
    description: 'Patterns and gentle observations from your check-ins.',
    route: '/insights',
    section: 'Home',
    icon: Sparkles,
    keywords: ['insights', 'patterns', 'recommendations', 'ai', 'observations'],
  },
  {
    id: 'reminders',
    title: 'My Reminders',
    description: 'Daily nudges for hydration, medication and habits.',
    route: '/my-reminders',
    section: 'Home',
    icon: Bell,
    keywords: ['reminders', 'nudges', 'alerts', 'notifications', 'schedule'],
  },
  {
    id: 'hydration',
    title: 'Hydration Tracking',
    description: 'Log water intake as part of your daily check-in.',
    route: '/smart-check-in',
    section: 'Home',
    icon: Droplet,
    keywords: ['hydration', 'water', 'drink', 'fluid', 'thirsty', 'glasses'],
  },

  // ===================================================================
  // NUTRITION — educational, culturally-aware
  // ===================================================================
  {
    id: 'nutrition',
    title: 'Smart Nutrition',
    description: 'Culturally-aware meal guidance and healthy swap ideas.',
    route: '/nutrition',
    section: 'Nutrition',
    icon: Salad,
    keywords: ['nutrition', 'food', 'meal', 'diet', 'eating', 'healthy', 'wellness'],
  },
  {
    id: 'healthy-local-discovery',
    title: 'Healthy Local Discovery',
    description: 'Personalized wellness food guidance by postcode/ZIP.',
    route: '/nutrition',
    section: 'Nutrition',
    icon: Apple,
    keywords: ['local', 'postcode', 'zip', 'discovery', 'nearby', 'area', 'region', 'ingredients'],
  },
  {
    id: 'healthy-swaps',
    title: 'Healthy Swap Ideas',
    description: 'Simple ingredient swaps for healthier meals.',
    route: '/nutrition',
    section: 'Nutrition',
    icon: Sparkles,
    keywords: ['swap', 'swaps', 'alternatives', 'substitute', 'healthier', 'replace'],
  },

  // ===================================================================
  // FAMILY — children, maternal, family hub
  // ===================================================================
  {
    id: 'family-health',
    title: 'Family Health Hub',
    description: 'Manage wellness for everyone in your household.',
    route: '/family-health',
    section: 'Family',
    icon: Users,
    keywords: ['family', 'household', 'members', 'kids', 'parents', 'together'],
  },
  {
    id: 'childrens-health',
    title: "Children's Health",
    description: 'Growth, immunisations, nutrition and behaviour tracking.',
    route: '/childrens-health',
    section: 'Family',
    icon: Baby,
    keywords: ['children', 'kids', 'child', 'baby', 'paediatric', 'pediatric', 'growth', 'school'],
  },
  {
    id: 'child-immunization',
    title: 'Immunisation Schedule',
    description: "Track your child's vaccination milestones.",
    route: '/childrens-health/immunization',
    section: 'Family',
    icon: ShieldCheck,
    keywords: ['immunization', 'immunisation', 'vaccine', 'vaccines', 'vaccination', 'shots', 'jab'],
  },
  {
    id: 'child-growth',
    title: 'Child Growth Tracker',
    description: 'Height, weight and growth curve tracking.',
    route: '/childrens-health/growth',
    section: 'Family',
    icon: TrendingUp,
    keywords: ['growth', 'height', 'weight', 'curve', 'percentile', 'development'],
  },
  {
    id: 'maternal-health',
    title: 'Maternal Health',
    description: 'Pregnancy, prenatal and postpartum wellness support.',
    route: '/maternal-health',
    section: 'Family',
    icon: Venus,
    keywords: ['maternal', 'pregnancy', 'prenatal', 'expecting', 'mother', 'pregnant', 'baby', 'trimester', 'postpartum'],
  },
  {
    id: 'maternal-week',
    title: 'Pregnancy Week-by-Week',
    description: "What's happening this week of your pregnancy.",
    route: '/maternal-health/week',
    section: 'Family',
    icon: Calendar,
    keywords: ['week', 'pregnancy', 'trimester', 'progress', 'maternal'],
  },
  {
    id: 'maternal-postpartum',
    title: 'Postpartum Support',
    description: 'Recovery, mood and newborn-care guidance.',
    route: '/maternal-health/postpartum',
    section: 'Family',
    icon: HeartPulse,
    keywords: ['postpartum', 'recovery', 'after birth', 'newborn', 'fourth trimester'],
  },
  {
    id: 'womens-health',
    title: "Women's Health",
    description: 'Cycle, hormonal and wellness guidance for women.',
    route: '/womens-health',
    section: 'Family',
    icon: Venus,
    keywords: ['women', 'womens', 'female', 'cycle', 'menstrual', 'period', 'menopause'],
  },
  {
    id: 'mens-health',
    title: "Men's Health",
    description: 'Targeted wellness guidance for men.',
    route: '/mens-health',
    section: 'Family',
    icon: Mars,
    keywords: ['men', 'mens', 'male', 'testosterone', 'prostate'],
  },

  // ===================================================================
  // SERVICES — future ecosystem previews (Pharmacy, Specialists, Wellness+)
  // ===================================================================
  {
    id: 'care-services',
    title: 'Care Services',
    description: 'Pharmacy, specialists and Wellness+ — coming soon.',
    route: '/care-services',
    section: 'Services',
    icon: Stethoscope,
    keywords: ['care', 'services', 'specialist', 'pharmacy', 'wellness+', 'wellness plus', 'doctor'],
  },
  {
    id: 'talk-to-specialist',
    title: 'Talk to a Specialist',
    description: 'Connect with a wellness specialist (coming soon).',
    route: '/care-services',
    section: 'Services',
    icon: Stethoscope,
    keywords: ['specialist', 'doctor', 'consultation', 'talk', 'expert', 'advice', 'professional', 'gp'],
    status: 'coming-soon',
  },
  {
    id: 'pharmacy',
    title: 'Medication & Pharmacy',
    description: 'Pharmacy partnerships and prescription support (coming soon).',
    route: '/care-services',
    section: 'Services',
    icon: Pill,
    keywords: ['pharmacy', 'medication', 'prescription', 'drug', 'drugs', 'meds', 'chemist', 'refill'],
    status: 'coming-soon',
  },
  {
    id: 'live-health-guidance',
    title: 'Live Health Guidance',
    description: 'On-demand wellness guidance from professionals (coming soon).',
    route: '/care-services',
    section: 'Services',
    icon: Sparkles,
    keywords: ['live', 'guidance', 'chat', 'on demand', 'professional', 'support'],
    status: 'coming-soon',
  },
  {
    id: 'wellness-plus',
    title: 'Wellness+ Membership',
    description: 'Premium wellness ecosystem preview (coming soon).',
    route: '/care-services',
    section: 'Services',
    icon: Sparkles,
    keywords: ['wellness+', 'wellness plus', 'premium', 'subscription', 'membership', 'plus'],
    status: 'coming-soon',
  },
  {
    id: 'medication-support',
    title: 'Medication Support',
    description: 'Track medication routines and reminders.',
    route: '/medications',
    section: 'Services',
    icon: Pill,
    keywords: ['medication', 'meds', 'pills', 'tablets', 'dose', 'dosage', 'reminder'],
  },

  // ===================================================================
  // SUPPORT — help, FAQ, education
  // ===================================================================
  {
    id: 'support-help',
    title: 'Support & Help',
    description: 'FAQs, technical help and how to use HealthLoop.',
    route: '/support-help',
    section: 'Support',
    icon: HelpCircle,
    keywords: ['support', 'help', 'faq', 'questions', 'how to', 'contact', 'issue', 'problem'],
  },
  {
    id: 'education-hub',
    title: 'Education Hub',
    description: 'Wellness articles, guides and learning content.',
    route: '/education',
    section: 'Support',
    icon: BookOpen,
    keywords: ['education', 'learn', 'articles', 'guides', 'hub', 'read', 'content'],
  },
  {
    id: 'health-risk-check',
    title: 'Health Risk Check',
    description: 'Lightweight self-assessment of wellness risk factors.',
    route: '/health-risk-check',
    section: 'Support',
    icon: ShieldCheck,
    keywords: ['risk', 'assessment', 'check', 'screening', 'evaluation'],
  },
  {
    id: 'symptom-checker',
    title: 'Symptom Checker',
    description: 'Informational symptom guidance — not a diagnostic tool.',
    route: '/symptom-checker',
    section: 'Support',
    icon: ClipboardList,
    keywords: ['symptom', 'symptoms', 'pain', 'unwell', 'sick', 'check', 'illness'],
  },
  {
    id: 'mental-health',
    title: 'Mental Wellness',
    description: 'Mood, stress and emotional wellbeing guidance.',
    route: '/mental-health',
    section: 'Support',
    icon: Brain,
    keywords: ['mental', 'mind', 'mood', 'stress', 'anxiety', 'depression', 'emotional', 'wellbeing', 'therapy', 'calm'],
  },
  {
    id: 'hormonal-health',
    title: 'Hormonal Health',
    description: 'Hormonal balance and related wellness guidance.',
    route: '/hormonal-health',
    section: 'Support',
    icon: Activity,
    keywords: ['hormonal', 'hormones', 'thyroid', 'balance', 'endocrine'],
  },
  {
    id: 'breathing',
    title: 'Breathing & Calm',
    description: 'Quick breathing exercises for stress relief.',
    route: '/mental-health',
    section: 'Support',
    icon: Wind,
    keywords: ['breathing', 'breath', 'calm', 'meditation', 'relax', 'stress relief'],
  },
  {
    id: 'preventive-health',
    title: 'Preventive Health',
    description: 'Screenings and preventive wellness checklists.',
    route: '/preventive-health',
    section: 'Support',
    icon: ShieldCheck,
    keywords: ['preventive', 'prevention', 'screening', 'checkup', 'screenings'],
  },

  // ===================================================================
  // PROFILE — account, privacy, settings
  // ===================================================================
  {
    id: 'profile',
    title: 'My Profile',
    description: 'Personal details, preferences and account settings.',
    route: '/profile',
    section: 'Profile',
    icon: User,
    keywords: ['profile', 'account', 'settings', 'me', 'personal', 'details'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Recent alerts and gentle wellness nudges.',
    route: '/notifications',
    section: 'Profile',
    icon: Bell,
    keywords: ['notifications', 'alerts', 'updates', 'messages', 'inbox'],
  },
  {
    id: 'reminders-settings',
    title: 'Reminder Settings',
    description: 'Customise reminder timing and frequency.',
    route: '/settings/reminders',
    section: 'Profile',
    icon: Bell,
    keywords: ['settings', 'reminders', 'preferences', 'schedule', 'timing'],
  },
  {
    id: 'cultural-settings',
    title: 'Cultural Preferences',
    description: 'Language and cultural adaptation preferences.',
    route: '/cultural-settings',
    section: 'Profile',
    icon: Languages,
    keywords: ['cultural', 'culture', 'language', 'region', 'localisation', 'localization', 'preferences', 'cuisine'],
  },
  {
    id: 'privacy',
    title: 'Privacy Notice',
    description: 'How HealthLoop handles your data.',
    route: '/privacy',
    section: 'Profile',
    icon: Lock,
    keywords: ['privacy', 'data', 'gdpr', 'security', 'policy'],
  },
];

/**
 * Suggested popular searches shown on the empty state — kept short and
 * mapped to high-traffic features so first-time users get a feel for what
 * search can actually do.
 */
export const POPULAR_SUGGESTIONS = [
  'steps',
  'hydration',
  'pharmacy',
  'pregnancy',
  'mental wellness',
  'medication',
  'specialist',
  'nutrition',
];
